import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import TextFilter from "../filters/TextFilter";
import SortHeader from "../sorting/SortHeader";
import { Link, useNavigate } from "react-router-dom";
import React from "react";
import Table, { Row, Cell } from "../../ui/Table";
import Text from "../../ui/Text";
import PropTypes from "prop-types";
import DropDownFilter from "../filters/DropDownFilter";
import { useSelector } from "react-redux";
import { selectRSOTypes, selectSpaceOrgs } from "./slice";
import SpaceOrganisation from "./SpaceOrganisation";
import RSOType from "./RSOType";
import { calculateOrbitType, toOrbitShortName } from "./orbit";
import RSOInfo from "./RSOInfo";
import AgeSince from "./AgeSince";
import Timestamp from "../../ui/Timestamp";
import RSOOrbitRegimeShort from "./RSOOrbitRegimeShort";
import { selectAdvancedMode } from "../settings/slice";
import RSOOrbitDetail from "./RSOOrbitDetail";
import IDCopy from "../../ui/IDCopy";
import RSOBrightness from "./RSOBrightness";

const RSOTable = ({
	rsos = [],
	filter,
	setFilter,
	selected = [],
	setSelected,
	enumerate = false,
}) => {
	const advancedMode = useSelector(selectAdvancedMode);
	const rosTypes = useSelector(selectRSOTypes); // The various types of RSO
	const spaceOrgs = useSelector(selectSpaceOrgs); // The various space organisations responsible for launching the object
	const navigate = useNavigate();
	const navigateTo = (id) => () => navigate(`/rsos/${id}`);
	const allSelected =
		rsos && selected && selected.length > 0 && selected.length == rsos.length;

	// Select individual item
	const toggleSelected = (rsoID) => {
		if (selected.includes(rsoID)) {
			setSelected(selected.filter((p) => p !== rsoID));
		} else {
			setSelected([...selected, rsoID]);
		}
	};

	// Select all items or none
	const toggleAllSelected = () => {
		if (allSelected) {
			setSelected([]);
		} else if (rsos) {
			setSelected([...rsos.map((rso) => rso.id)]);
		}
	};

	return (
		<Table>
			<TableHeaderRow sticky>
				{/* Optional 'select all' */}
				{enumerate && <TableHeader>#</TableHeader>}
				{setSelected != null && (
					<TableHeader>
						<input
							type="checkbox"
							checked={allSelected}
							onChange={toggleAllSelected}
							title="Select all / none"
						/>
					</TableHeader>
				)}
				<TableHeader
					filterControl={
						filter && (
							<TextFilter
								filterByName="object_cnts"
								filter={filter}
								setFilter={setFilter}
								placeholder="name or norad ids"
								active={filter.object_cnts ? true : false}
							/>
						)
					}
					sortControl={
						(filter && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"object_name"}
							/>
						)) ||
						undefined
					}
				>
					Object
				</TableHeader>
				<TableHeader
					filterControl={
						filter && (
							<TextFilter
								filterByName="cospar_cnts"
								filter={filter}
								setFilter={setFilter}
								active={filter.cospar_cnts ? true : false}
							/>
						)
					}
					sortControl={
						filter && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"cospar_id"}
							/>
						)
					}
				>
					COSPAR ID
				</TableHeader>
				<TableHeader>Orbit Type</TableHeader>
				<TableHeader
					filterControl={
						filter && (
							<DropDownFilter
								values={rosTypes.map((rso) => {
									return { name: rso, value: rso };
								})}
								filterByName="object_type"
								filter={filter}
								setFilter={setFilter}
								active={filter.object_type ? true : false}
							/>
						)
					}
					sortControl={
						filter && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"object_type"}
							/>
						)
					}
				>
					Object type
				</TableHeader>
				<TableHeader>Brightness</TableHeader>
				<TableHeader
					filterControl={
						filter && (
							<DropDownFilter
								values={
									spaceOrgs &&
									spaceOrgs.map((so) => {
										return { name: so.name, value: so.code };
									})
								}
								filterByName="org_code"
								filter={filter}
								setFilter={setFilter}
								active={filter.org_code ? true : false}
							/>
						)
					}
					sortControl={
						filter && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"org_code"}
							/>
						)
					}
				>
					Origin
				</TableHeader>
				<TableHeader
					sortControl={
						filter && (
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"launch_date"}
							/>
						)
					}
				>
					Launch Date
				</TableHeader>
				<TableHeader
					filterControl={
						filter && (
							<DropDownFilter
								values={["user", "spacetrack"].map((so) => {
									return { name: so, value: so };
								})}
								filterByName="source"
								filter={filter}
								setFilter={setFilter}
								active={filter.source ? true : false}
							/>
						)
					}
				>
					Source
				</TableHeader>
				<TableHeader>TLE epoch</TableHeader>
				{/*Actions*/}
				<TableHeader />
			</TableHeaderRow>
			{rsos && (
				<tbody>
					{rsos.map((rso, i) => (
						<Row key={rso.id} zebra>
							{enumerate && (
								<Cell>
									<Text small secondary>
										{i + 1}.
									</Text>
								</Cell>
							)}
							{setSelected != null && (
								<Cell>
									<input
										type="checkbox"
										checked={selected.includes(rso.id)}
										onChange={() => toggleSelected(rso.id)}
									/>
								</Cell>
							)}
							<Cell onClick={navigateTo(rso.id)}>
								{" "}
								<RSOInfo
									key={rso.id}
									{...rso}
									object_type={undefined}
									object_id={undefined}
								/>
							</Cell>
							<Cell onClick={navigateTo(rso.id)}>
								<IDCopy id={rso?.object_id} showCopy />
							</Cell>
							<Cell>
								<div>
									<RSOOrbitRegimeShort {...rso} />
								</div>
								{advancedMode && (
									<div>
										<RSOOrbitDetail {...rso} />
									</div>
								)}
							</Cell>
							<Cell>
								<RSOType type={rso.object_type} printName />
							</Cell>
							<Cell>
								<RSOBrightness {...rso} />
							</Cell>
							<Cell>
								<SpaceOrganisation code={rso.country_code} />
							</Cell>
							<Cell>
								<Timestamp time={false}>{rso.launch_date}</Timestamp>
							</Cell>

							<Cell>
								<Text small new={rso.source === "user"}>
									{rso.source}
								</Text>
							</Cell>
							<Cell>
								<AgeSince {...rso} />
							</Cell>
							<Cell>
								<Link to={`/rsos/${rso.id}`}>Detail</Link>
							</Cell>
						</Row>
					))}
				</tbody>
			)}
		</Table>
	);
};

RSOTable.propTypes = {
	rsos: PropTypes.array, // the rsos to render
	filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
	setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
	selected: PropTypes.array, // Optional List<RSOID>
	setSelected: PropTypes.func, // Optional func(List<RSOID>)
	enumerate: PropTypes.bool, // If true, number the items in the table
};

export default RSOTable;
